import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { useIsMobile } from "@/hooks/use-mobile"
import { useOnline } from "@/hooks/use-online"

/**
 * OKS-19 F9 — UnblocklistConfirmation modal (no-reason variant).
 *
 * **OKS-Admin-only affordance** (UX Spec joint freeze + BE TRD §Key
 * Decisions). The button (`candidate-detail-unblocklist`) is rendered
 * by F7's CandidateActionBar only when `actions.unblocklist_url !== null`,
 * which BE serializes only for OKS-Admin AND status == "blocklisted".
 *
 * **No reason field** (UX Spec — joint freeze with ux-oks19, confirmed
 * during Phase 2 cross-Lead coordination). Lifting a blocklist is
 * the inverse of blocking, but PRD doesn't require a reason for the
 * lift — it's a normal corrective transition. Submit variant remains
 * destructive (red) because lifting blocklist DOES have cross-brand
 * implications (the creator becomes available for approval again);
 * UX Spec calls for the destructive visual cue to slow the user
 * down even though no reason is captured.
 *
 * **Wire format (BE TRD lock — nested strong params):**
 *   POST /candidates/:id/transitions
 *   body: { transition: { event: "unblocklist" } }
 *
 * **Locked testids (FE TRD §testid Map):**
 *   - `candidate-detail-unblocklist-dialog` — modal root
 *   - `candidate-detail-unblocklist-confirm` — destructive submit
 *   - `candidate-detail-unblocklist-cancel` — ghost cancel
 */
export interface UnblocklistConfirmationProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  actionUrl: string
  onSuccess?: () => void
}

export function UnblocklistConfirmation({
  open,
  onOpenChange,
  actionUrl,
  onSuccess,
}: UnblocklistConfirmationProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const online = useOnline()

  const formContent = (
    <Form
      action={actionUrl}
      method="post"
      options={{ preserveScroll: true }}
      onSuccess={() => onSuccess?.()}
    >
      {({ processing }) => (
        <>
          <input type="hidden" name="transition[event]" value="unblocklist" />
          {isMobile ? (
            <DrawerFooter className="px-0">
              <Button
                type="submit"
                variant="destructive"
                data-testid="candidate-detail-unblocklist-confirm"
                disabled={processing || !online}
              >
                {t("candidates.detail.actions.unblocklist_confirm")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-detail-unblocklist-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.detail.actions.unblocklist_cancel")}
              </Button>
            </DrawerFooter>
          ) : (
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                data-testid="candidate-detail-unblocklist-cancel"
                disabled={processing}
                onClick={() => onOpenChange(false)}
              >
                {t("candidates.detail.actions.unblocklist_cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                data-testid="candidate-detail-unblocklist-confirm"
                disabled={processing || !online}
              >
                {t("candidates.detail.actions.unblocklist_confirm")}
              </Button>
            </DialogFooter>
          )}
        </>
      )}
    </Form>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent data-testid="candidate-detail-unblocklist-dialog">
          <DrawerHeader>
            <DrawerTitle>
              {t("candidates.detail.actions.unblocklist_dialog_title")}
            </DrawerTitle>
            <DrawerDescription>
              {t("candidates.detail.actions.unblocklist_dialog_description")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">{formContent}</div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="candidate-detail-unblocklist-dialog"
        className="sm:max-w-[480px]"
      >
        <DialogHeader>
          <DialogTitle>
            {t("candidates.detail.actions.unblocklist_dialog_title")}
          </DialogTitle>
          <DialogDescription>
            {t("candidates.detail.actions.unblocklist_dialog_description")}
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  )
}
